import React, { useState } from 'react'
import { Card, Badge } from '../../components/UI'
import { GitBranch, Tag, Calendar, ChevronDown, ChevronUp, Rocket } from 'lucide-react'

// Historial de versiones embebido (fuente de verdad)
const VERSIONS = [
    {
        version: '4.7.2',
        type: 'patch',
        date: '2026-02-22',
        title: 'Fix: Resolución Bloqueo de UI por PROD',
        features: [
            '🐛 Fix: vendor_core.ts verificaba env.PROD de manera dinámica, evadiendo el reemplazo de build_drm.cjs. Ahora usa import.meta.env directamente para asegurar el bloqueo en producción si hay fallo de licencia.'
        ]
    },
    {
        version: '4.7.1',
        type: 'patch',
        date: '2026-02-22',
        title: 'Fix: Telemetría Robusta y Ofuscación de Integración',
        features: [
            '🐛 Fix: vendor_core.ts ahora reintenta enviar telemetría si el RPC responde con status no-ok.',
            '🔒 Optimización: build_drm.cjs previene la doble ofuscación y ya no congela el navegador en producción al desactivar reglas agresivas.',
            '📝 Manuales: Actualización a las INSTRUCCIONES.md prohibiendo chequeos síncronos del DRM en montajes iniciales de React.'
        ]
    },
    {
        version: '4.7.0',
        type: 'minor',
        date: '2026-02-22',
        title: 'Bloque 5 — Circuit Breaker, Sidebar Agrupado & FeatureNotEnabled',
        features: [
            '⚡ Circuit Breaker real en vendor_core.ts: 3 estados (closed/open/half-open), umbrales configurables, window.__tower_cb',
            '📂 Sidebar reorganizado en 5 grupos colapsables con emojis (desde navItems.ts)',
            '🚧 Componente FeatureNotEnabled: banner reutilizable para 14 páginas de referencia',
            '📦 Integration Kit actualizado: INSTRUCCIONES.md con Módulo 0 (CB) + vendor_core.js regenerado',
            '🐛 Fix: AppShell usaba NAV_ITEMS inexistente → refactorizado a usar componente Sidebar con NAV_GROUPS',
        ]
    },
    {
        version: '4.6.0',
        type: 'minor',
        date: '2026-02-22',
        title: 'Snapshots Automáticos & Rollback Remoto',
        features: [
            '📸 SnapshotsPage: sistema de backups con rollback, descarga y eliminación',
            '⚙️ Config: auto-snapshot, retención, encrytpión AES-256',
            '📖 Docs: proceso rollback en 5 pasos + precauciones',
            '🔧 Sidebar: 24 items — 100% ideas.md implementadas',
        ]
    },
    {
        version: '4.5.0',
        type: 'minor',
        date: '2026-02-22',
        title: 'Fase 5 — Suite de Seguridad Anti-piratería',
        features: [
            '🔏 WatermarkPage: 6 técnicas de watermarking invisible, demo visual, config y docs',
            '🛡️ IntegrityPage: verificación anti-tampering con SRI, 5 archivos, estrategia 4 pasos',
            '🔐 CspPage: generador CSP, 10 directivas, copiar header, guía ataques + Vercel deploy',
            '🔧 Sidebar: 23 items (3 nuevos: Watermarking, Integridad, CSP Policy)',
        ]
    },
    {
        version: '4.4.0',
        type: 'minor',
        date: '2026-02-22',
        title: 'Fase 4 — Herramientas de Auditoría',
        features: [
            '♿ AccessibilityPage: scanner WCAG 2.1 AA con score circular, 10 reglas, checklist principios',
            '📦 BundlePage: distribución chunks con barras, detección dead code, tips de optimización',
            '🔑 SecretsPage: scanner de secrets expuestos, variables de entorno con niveles de riesgo',
            '🔧 Sidebar: 20 items (3 nuevos: Accesibilidad, Bundle Analyzer, Secrets Scanner)',
        ]
    },
    {
        version: '4.3.0',
        type: 'minor',
        date: '2026-02-22',
        title: 'Fase 3 — Ofuscación Extrema y Conexión Persistente',
        features: [
            '📡 WebSocketsPage: monitor de Supabase Realtime, event stream, canales, guía migración polling→WS',
            '🔩 WasmPage: estado de módulos Wasm planificados, benchmarks JS vs Wasm, arquitectura y roadmap',
            '🌐 ServiceWorkerPage: interceptor de red, cache storage, reglas de interceptación, estrategias',
            '🔐 EncryptionPage: AES-256-GCM domain-bound, dominios autorizados, demo Web Crypto API',
            '🔧 Sidebar: 17 items (4 nuevos: WebSockets, WebAssembly, Service Worker, Encriptación)',
        ]
    },
    {
        version: '4.2.0',
        type: 'minor',
        date: '2026-02-22',
        title: 'Fase 2 — Control, Resiliencia y Flexibilidad',
        features: [
            '🎛️ FeatureFlagsPage.tsx: gestión visual de Feature Flags JSONB por licencia',
            '🎛️ Toggle on/off con 8 flags predefinidos (maintenance, dark_mode, beta, debug, etc.)',
            '🎛️ Historial de cambios de sesión + vista JSON raw del campo features',
            '⚡ CircuitBreakerPage.tsx: monitor visual del patrón Circuit Breaker',
            '⚡ Diagrama de estados interactivo (CLOSED → OPEN → HALF-OPEN)',
            '⚡ Simulador de fallos/éxitos/timeouts + log de transiciones',
            '⚡ Configuración de umbrales (threshold, timeout, probes, window)',
            '📊 PerformancePage.tsx: dashboard de Core Web Vitals con budgets',
            '📊 5 métricas: LCP, FID/INP, CLS, TTFB, FCP con indicadores semáforo',
            '📊 Score global circular, sparklines de tendencia, recomendaciones de optimización',
            '🔧 Sidebar: 13 items (3 nuevos: Feature Flags, Circuit Breaker, Performance)',
        ]
    },
    {
        version: '4.1.0',
        type: 'minor',
        date: '2026-02-22',
        title: 'Fase 1 — Legal, Transparencia y Security Audit',
        features: [
            '📄 ContractsPage.tsx: generador de contratos RGPD con 7 cláusulas pre-redactadas',
            '📄 Preview en iframe live + export HTML y PDF (via print dialog)',
            '👁️ TransparencyPage.tsx: dashboard RGPD Art. 15 con selector de licencia',
            '👁️ Datos recopilados por categoría (sesiones, pageviews, errores, performance)',
            '👁️ Historial de comandos remotos por licencia + export JSON (Art. 20)',
            '👁️ Panel de derechos RGPD (acceso, rectificación, supresión, portabilidad, oposición)',
            '🛡️ SecurityPage.tsx: 5 tabs de auditoría de seguridad',
            '🛡️ Tab Mejoras: progreso por fase (Fase 0-3) con badges de estado',
            '🛡️ Tab Prompts IA: plantillas copiables para auditorías con IA',
            '🛡️ Tab Legal: estado de cumplimiento RGPD (8 artículos verificados)',
            '🛡️ Tab Vulnerabilidades: log CVE-like con severidad y resolución',
            '🛡️ Tab Checklist: verificación interactiva pre-deploy con barra de progreso',
            '🔄 Migración completa JSX → TSX (App, main, 10 pages, lib, data)',
            '🔧 Sidebar: 10 items (3 nuevos: Contratos, Transparencia, Security Audit)',
            '🐛 Fix: AppShell usaba lista navItems hardcodeada → ahora usa NAV_ITEMS global',
        ]
    },
    {
        version: '4.0.0',
        type: 'major',
        date: '2026-02-22',
        title: 'Security Hardening — Fase 0',
        features: [
            '🔒 vendor_core.ts: migración completa a TypeScript (strict mode)',
            '🔒 Eliminado monkey-patching de window.fetch → PerformanceObserver',
            '🔒 Comando redirect con whitelist de dominios (allowed_redirect_domains)',
            '🔒 clear_cache selectivo: solo borra claves __tower_*, __lc, __sid',
            '🔒 HMAC: firma digital para comandos remotos (tower_send_command)',
            '🔒 RLS reforzado en remote_commands, command_audit_log, active_sessions',
            '📊 grace_days configurable por licencia (0-90 días, default 15)',
            '📊 Degradación gradual: aviso admin → solo lectura → nunca bloqueo inmediato',
            '📊 validate_license_v2 retorna grace_days + allowed_redirect_domains',
            '⚡ Polling 60s con backoff exponencial, heartbeat 5min, Visibility API',
            '🛡️ Telemetría minimizada (RGPD Art. 5.1.c): sin UA, sin resolución, sin timezone',
            '🛡️ Sanitización de URLs y stack traces (redacta emails, tokens, JWTs)',
            '💾 Nueva tabla: command_audit_log (auditoría de comandos remotos)',
            '💾 Nuevas columnas: remote_commands.expires_at, .hmac_signature, .acknowledged_at',
            '💾 build_drm.cjs: usa esbuild para transpilar TypeScript antes de ofuscar',
        ]
    },
    {
        version: '3.2.5',
        type: 'patch',
        date: '2026-02-15',
        title: 'UI Fix: Mobile Actions',
        features: [
            'Mobile: Added missing "Extend License" (Calendar) button',
            'UI: Unified action buttons between Desktop and Mobile views'
        ]
    },
    {
        version: '3.2.4',
        type: 'patch',
        date: '2026-02-15',
        title: 'Cache Buster: Force Refresh',
        features: [
            'System: Force cache validation to apply system fonts',
            'SW: Updated cache version to v4 to purge stale assets'
        ]
    },
    {
        version: '3.2.3',
        type: 'patch',
        date: '2026-02-15',
        title: 'Performance: System Fonts',
        features: [
            'Performance: Removed Google Fonts dependency',
            'UI: Switched to native system fonts for better performance',
            'Security: Simplified Content Security Policy (CSP)'
        ]
    },
    {
        version: '3.2.2',
        type: 'hotfix',
        date: '2026-02-15',
        title: 'Hotfix: Service Worker CSP',
        features: [
            'Fix: Service Worker blocked from fetching Google Fonts (connect-src)',
            'Security: Expanded CSP to allow fonts.googleapis.com in connect specific directive'
        ]
    },
    {
        version: '3.2.1',
        type: 'patch',
        date: '2026-02-15',
        title: 'Hotfix: CSP & Deployment',
        features: [
            'Fix: Content-Security-Policy blocking Google Fonts',
            'Fix: _headers configuration for Cloudflare Pages',
            'Meta: Updated mobile-web-app-capable tags',
            'Build: Production artifacts regeneration'
        ]
    },
    {
        version: '3.2.0',
        type: 'minor',
        date: '2026-02-15',
        title: 'Intranet Legacy + Concurrencia',
        features: [
            'Soporte Intranet Legacy: módulos (Noticias, Buzón, RRHH) en panel de licencias',
            'Validación de Dominios Estricta en RPCs validate_license',
            'Nuevo comando clear_cache para revocación instantánea',
            'Control de Concurrencia: tabla active_sessions + validate_license_v2 optimizado',
        ]
    },
    {
        version: '3.1.0',
        type: 'minor',
        date: '2026-02-15',
        title: 'Diagnóstico y Herramientas Post-Debug',
        features: [
            'RPCs SECURITY DEFINER: ai_diagnose_license, ai_simulate_traffic, ai_get_license_active_count',
            'Contador usuarios activos (últimos 5 min) en página de Licencias',
            'Expiración Inteligente: colores verde/naranja/amarillo/rojo/gris',
            'UX: soft-warning (toast + doble click) en vez de confirm() nativo',
            'Botón de Diagnóstico (📉) con estado real de la DB',
        ]
    },
    {
        version: '3.0.0',
        type: 'major',
        date: '2026-02-15',
        title: 'Centro de Mando Remoto — Rediseño Total',
        features: [
            'Sidebar persistente con navegación global (8 secciones)',
            'Dashboard con 6 KPIs en tiempo real',
            'Telemetría: sesiones, pageviews, navegadores, performance',
            'Error Tracking: captura de errores JS, fetch, promesas rechazadas',
            'Centro de Comandos: mantenimiento, refresh, mensajes, redirect, diagnósticos',
            'Broadcasts: feature toggles, extensiones, mensajes vía RPCs reales',
            'Historial de versiones de la propia Torre',
            'vendor_core.js v3: agente espía completo (5 módulos ofuscados)',
            'PWA: instalable en móvil con service worker',
            'Favicon SVG con escudo + branding "Control Tower"',
            '3 manuales actualizados (Usuario, Técnico, Comandos IA)',
            'Tablas nuevas: telemetry_events, remote_commands',
            'RPCs: tower_report_event, tower_poll_commands, tower_ack_command, tower_send_command'
        ]
    },
    {
        version: '2.1.1',
        type: 'hotfix',
        date: '2026-02-15',
        title: 'Correcciones Críticas en Producción',
        features: [
            'RPC Robustness: validate_license_v1 acepta TEXT con casting interno',
            'Build Security: build_drm.cjs con credenciales de respaldo',
            'Schema Fix: Columnas faltantes en validation_logs',
            'Git Submodule Fix: Eliminado gitlink corrupto',
        ]
    },
    {
        version: '2.1.0',
        type: 'minor',
        date: '2026-02-15',
        title: 'Sistema de Mensajería (Broadcast)',
        features: [
            'Columna message en licenses',
            'validate_license_v1 devuelve campo message',
            'vendor_core.js emite mensajes en evento DRM_VALID',
        ]
    },
    {
        version: '2.0.0',
        type: 'major',
        date: '2026-02-15',
        title: 'Ultimate Control Tower',
        features: [
            'Features y Quotas en licencias (JSONB)',
            'Tabla project_versions para versionado',
            'Vistas AI (system_health, licenses_detail, recent_logs)',
            'RPCs: ai_revoke_license, ai_toggle_feature, ai_extend_license, ai_publish_version',
            'Módulo de Documentación in-app (Markdown)',
            'Módulo de Versiones',
            'Heartbeat con enforce_active_license trigger',
            'Headers de seguridad para Cloudflare'
        ]
    },
    {
        version: '1.1.0',
        type: 'minor',
        date: '2026-02-14',
        title: 'Sistema de Mensajería y Broadcasts',
        features: [
            'RPC ai_send_broadcast para mensajes masivos',
            'Tabla de logs de validación',
            'Documentación de integración para agentes IA',
            'Deploy en Cloudflare Pages'
        ]
    },
    {
        version: '1.0.0',
        type: 'major',
        date: '2026-02-14',
        title: 'Versión Inicial',
        features: [
            'Login con autenticación Supabase',
            'Dashboard con lista de proyectos y licencias',
            'Crear, revocar y gestionar licencias',
            'Validación DRM con vendor_core.js',
            'Bloqueo visual en producción si licencia inválida',
            'Grace period de 72h con caché local'
        ]
    }
]

const typeColors = {
    major: 'from-red-500 to-orange-500',
    minor: 'from-blue-500 to-cyan-500',
    patch: 'from-green-500 to-emerald-500',
    hotfix: 'from-yellow-500 to-orange-500'
}

const typeBadge = {
    major: 'bg-red-500/10 text-red-400 border-red-500/20',
    minor: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    patch: 'bg-green-500/10 text-green-400 border-green-500/20',
    hotfix: 'bg-orange-500/10 text-orange-400 border-orange-500/20'
}

export default function VersionsPage() {
    const [expandedIdx, setExpandedIdx] = useState(0)

    return (
        <div className="p-6 max-w-4xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <GitBranch className="text-neon-blue" /> Historial de Versiones
                </h1>
                <p className="text-gray-500 text-sm">Changelog del Control Tower — {VERSIONS.length} releases</p>
            </div>

            {/* Timeline */}
            <div className="relative">
                <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-neon-blue via-purple-500 to-transparent"></div>
                <div className="space-y-6">
                    {VERSIONS.map((v, i) => {
                        const isExpanded = expandedIdx === i
                        const isLatest = i === 0
                        return (
                            <div key={v.version} className="relative pl-14">
                                {/* Timeline dot */}
                                <div className={`absolute left-[18px] w-3 h-3 rounded-full z-10 border-2 border-obsidian ${isLatest ? 'bg-neon-green animate-pulse' : 'bg-neon-blue'}`}></div>

                                <Card className="overflow-hidden hover:border-white/10 transition-all duration-300">
                                    <button
                                        onClick={() => setExpandedIdx(isExpanded ? -1 : i)}
                                        className="w-full text-left p-5"
                                        aria-label={`Ver detalles de v${v.version}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                {isLatest && <Rocket size={16} className="text-neon-green" />}
                                                <Tag size={16} className="text-neon-blue" />
                                                <span className="text-xl font-bold text-white">v{v.version}</span>
                                                <span className={`px-2 py-0.5 rounded text-xs font-mono border ${typeBadge[v.type]}`}>
                                                    {v.type}
                                                </span>
                                                {isLatest && (
                                                    <span className="px-2 py-0.5 rounded text-xs font-mono bg-neon-green/10 text-neon-green border border-neon-green/20">
                                                        latest
                                                    </span>
                                                )}
                                            </div>
                                            {isExpanded ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
                                        </div>
                                        <p className="text-gray-400 text-sm mt-1 font-medium">{v.title}</p>
                                        <div className="flex items-center gap-2 text-xs text-gray-600 mt-2">
                                            <Calendar size={12} />
                                            {new Date(v.date).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </div>
                                    </button>

                                    {/* Expandido: lista de features */}
                                    {isExpanded && (
                                        <div className="border-t border-white/5 px-5 py-4 bg-white/[0.01]">
                                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Cambios incluidos</p>
                                            <ul className="space-y-2">
                                                {v.features.map((f, fi) => (
                                                    <li key={fi} className="flex items-start gap-2 text-sm text-gray-300">
                                                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full bg-gradient-to-r ${typeColors[v.type]} flex-shrink-0`}></span>
                                                        {f}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </Card>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
